/**
 * callRecordingSyncService.js
 *
 * Responsible for uploading pending recordings from IndexedDB to S3,
 * then notifying the backend (SAVE_RECORDING) to persist the recording URL
 * on the ticket — same flow as twilioRequestVoice / handleRecordingCallback.
 *
 * Sync triggers:
 *  1. Immediately after a call ends (called from useWebRTC)
 *  2. On page load / app init (recover from browser crash / network loss)
 *  3. On "online" event (recover from offline)
 *
 * Retry: failed uploads are retried on next sync cycle.
 */

import { Storage } from 'aws-amplify';
import * as callRecordingDB from '@/common/utils/callRecordingDB';
import * as openAIWebRTCService from '@/service/openAIWebRTCService';
import localStorageUtil from '@/common/utils/localStorageUtil';

let isSyncing = false;

/**
 * Upload a single recording Blob to S3 and notify backend.
 * S3 key pattern: public/recordings/{corpId}/{ticketId}/{filename}
 */
const uploadSingleRecording = async (recording) => {
    const { callId, corpId, projectId, userId, ticketId, blob } = recording;

    // Validate ticketId — without it, recording cannot be linked to any ticket
    if (!ticketId) {
        await callRecordingDB.updateRecordingStatus(callId, 'failed');
        return false;
    }

    const affiliationOrg = corpId || localStorageUtil.getAffiliationOrganization();
    // Follow same path pattern as Twilio: public/{org}/ticket/{ticketId}/transcript_audio_{ticketId}.webm
    const recordingFilename = `transcript_audio_${ticketId}.webm`;
    const s3Key = `${affiliationOrg}/ticket/${ticketId}/${recordingFilename}`;

    try {
         // 1. Mark as uploading
        await callRecordingDB.updateRecordingStatus(callId, 'uploading');

        // 2. Upload to S3 via Amplify Storage
        // No ACL — same as Twilio flow (S3Helper.putObject uses default private ACL)
        // level: 'public' puts file under public/ prefix, accessible via Storage.get
        await Storage.put(s3Key, blob, {
            contentType: 'audio/webm',
            level: 'public',
        });

        // 3. Notify backend — same pattern as twilioRequestVoice / handleRecordingCallback
        //    Backend will update telephone_call_recording_url on ticket
        //    S3 full path = public/{s3Key} (Amplify Storage.put with level 'public' adds the prefix)
        if (corpId && projectId && userId) {
            await openAIWebRTCService.saveRecording(corpId, projectId, userId, {
                filename: recordingFilename,
                s3Key: `public/${s3Key}`,
                ticketId,
            });
        }

        // 4. Mark as uploaded & clean up from IndexedDB
        await callRecordingDB.updateRecordingStatus(callId, 'uploaded');
        await callRecordingDB.deleteRecording(callId);
        return true;
    } catch (err) {
        await callRecordingDB.updateRecordingStatus(callId, 'failed');
        return false;
    }
};

const syncPendingEndCalls = async () => {
    try {
        const allMeta = await callRecordingDB.getAllCallMeta();
        if (!allMeta.length) return;

        for (const meta of allMeta) {
            if (meta.isAppIntegration) {
                await callRecordingDB.deleteCallMeta(meta.callId);
                continue;
            }

            if (!meta.ticketId || !meta.corpId || !meta.projectId || !meta.userId) {
                await callRecordingDB.deleteCallMeta(meta.callId);
                continue;
            }
            try {
                // Cap duration to avoid inflated values when recovering after a crash
                // (callStartTime is from a previous session, so elapsed time includes downtime)
                const MAX_CALL_DURATION_SECONDS = 3600; // 1 hour cap
                const duration = meta.callStartTime && meta.callEndTime
                    ? Math.floor((meta.callEndTime - meta.callStartTime) / 1000)
                    : meta.callStartTime
                    ? Math.min(Math.floor((Date.now() - meta.callStartTime) / 1000), MAX_CALL_DURATION_SECONDS)
                    : 0;

                await openAIWebRTCService.endCall(meta.corpId, meta.projectId, meta.userId, {
                    ticketId: meta.ticketId,
                    chatThreadId: meta.chatThreadId,
                    duration,
                    aiTrainingChatThreadId: meta.aiTrainingChatThreadId,
                    aiOperatorTicketId: meta.aiOperatorTicketId,
                    logSif: [],
                });

                await callRecordingDB.deleteCallMeta(meta.callId);
            } catch (err) {
                // Will retry on next sync cycle
            }
        }
    } catch (err) {
        // silent
    }
};

export const syncPendingRecordings = async () => {
    if (isSyncing) return;

    isSyncing = true;
    try {
        const pending = await callRecordingDB.getPendingRecordings();
        if (!pending.length) return;

        for (const rec of pending) {
            await uploadSingleRecording(rec);
        }
    } catch (err) {
        // silent
    } finally {
        isSyncing = false;
    }
};

const recoverAndSync = async () => {
    try {
        await callRecordingDB.finalizeOrphanedChunks();
    } catch (err) {
        // silent
    }

    await syncPendingEndCalls();
    await syncPendingRecordings();
};

export const initRecordingSyncListeners = () => {
    recoverAndSync();

    window.addEventListener('online', () => {
        syncPendingRecordings();
    });

    // Sync on visibility change (tab becomes visible again)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            syncPendingRecordings();
        }
    });
};
